import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    CAlert,
    CButton,
    CDropdown,
    CDropdownDivider,
    CDropdownItem,
    CDropdownMenu,
    CDropdownToggle,
    CInput,
    CModal,
    CModalBody,
    CModalHeader,
    CModalTitle,
    CSelect,
    CSpinner,
    CSwitch
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {useHistory, useParams} from "react-router-dom";
import {getApi} from "../../../apis/backendApis";
import {retrieveToken} from "../../../services/tokenVerify";
import useAlert from "../../../services/useAlert";
import ConfirmModal from "../../../components/confirmModal";
import {
    StackExecuteTask,
    StackExecuteTaskAIList,
    StackExecuteTaskList,
    LIMITE_SIMULTANEO_PADRAO,
    TypeExecuteTask,
    TypeExecuteTaskResultList
} from "./index";
import {
    abrirCypressLocal,
    enviarCasosExecutorLocal,
    EXECUTOR_LOCAL_URL,
    EXECUTOR_SELENIUM_URL,
    executarLegadoUmPorVez
} from "./executorClient";
import {
    aguardaEntregaLocal,
    contarHistorico,
    duracaoResultado,
    ExecutionResult,
    isAtivo,
    isFalha,
    normalizarBusca,
    resumoSituacoes,
    Situacao,
    SITUACOES,
    situacaoResultado,
    textoBusca,
    ultimaExecucaoPorCaso
} from "./executionResults";
import useExecutionPolling from "./useExecutionPolling";
import ResultLogModal, {ResultLog} from "./resultLogModal";
import '../../../scss/styles-tester.scss';

interface GetParam {
    id?: string
}

interface Plano {
    id: number | string,
    name: string,
    type: number,
    stack: number,
    testCase: Array<any>,
    mockup: Array<any>,
    gerarRelatorio: boolean,
    executarSimultaneo: boolean,
    limiteSimultaneo: number
}

type Confirmacao =
    | {tipo: 'cancelar'}
    | {tipo: 'excluirTodos'}
    | {tipo: 'excluir', resultado: ExecutionResult};

type FiltroSituacao = 'todos' | Situacao;
type OrdemResultados = 'recentes' | 'plano' | 'erros';

/** Registro (log) de um resultado aberto em "Detalhes" */
interface RegistroDetalhe {
    carregando: boolean,
    erro: boolean,
    dados: ResultLog | null,
    /** Situação e contadores do resultado quando o registro foi lido: mudou, lê de novo */
    versao: string
}

const PREFERENCIA_JANELA = 'tester.abrirJanelaNavegador';
// Linhas do registro que explicam uma falha (as mesmas que o modal do registro pinta de vermelho/amarelo)
const MENSAGEM_FALHA = /\[ERRO]|Detalhe:|Payload inválido|interrompid|cancelad|encerrada sem|tempo limite|Executor indispon/i;
const MAXIMO_MENSAGENS_FALHA = 20;

// Filtros sempre visíveis; os demais aparecem só quando há resultado naquela situação
const SITUACOES_EXTRAS: Array<Situacao> = ['fila', 'executando', 'interrompido', 'cancelado'];
const ORDEM_DISTRIBUICAO: Array<Situacao> = ['sucesso', 'erro', 'interrompido', 'cancelado'];
const ICONE_SITUACAO: Record<Situacao, string | null> = {
    fila: 'cil-clock',
    executando: null,
    sucesso: 'cil-check-circle',
    erro: 'cil-x-circle',
    cancelado: 'cil-ban',
    interrompido: 'cil-warning',
};

const lerPreferenciaJanela = () => {
    try {
        return window.localStorage.getItem(PREFERENCIA_JANELA) === '1';
    } catch (e) {
        return false;
    }
}

const plural = (quantidade: number, singular: string, varios: string) => quantidade + ' ' + (quantidade === 1 ? singular : varios);

const TOTAIS: Record<Situacao, (quantidade: number) => string> = {
    sucesso:      n => plural(n, 'sucesso', 'sucessos'),
    erro:         n => n + ' com erro',
    interrompido: n => plural(n, 'interrompido', 'interrompidos'),
    cancelado:    n => plural(n, 'cancelado', 'cancelados'),
    executando:   n => n + ' executando',
    fila:         n => n + ' na fila',
};

const versaoDoResultado = (resultado: ExecutionResult) => resultado.status + ':' + resultado.amount_success + ':' + resultado.amount_erros;

const nomeDoCaso = (resultado: ExecutionResult) => resultado.nome || resultado.testCase || ('Resultado #' + resultado.id);

// ---------------------------------------------------------------- estilos da tela
// Ficam aqui, presos em .tester-exec, para valer só nesta tela

const ESTILOS = `
.tester-page.tester-exec {
    /* sem gap: os modais fechados deixam <div>s aqui e cada um ganharia o espaço entre itens, roubando altura da lista */
    gap: 0;
    padding: 1.5rem;
    color: #2f3a4a;
    font-size: .9375rem;
}
.tester-page.tester-exec > .exec-cabecalho, .tester-page.tester-exec > .exec-barra, .tester-page.tester-exec > .alert { margin-bottom: 1.25rem; }
.tester-exec .btn { display: inline-flex; align-items: center; justify-content: center; font-size: .9375rem; white-space: nowrap; }
.tester-exec .btn-sm { padding: .3rem .7rem; font-size: .9375rem; }
.tester-exec .btn .c-icon { flex-shrink: 0; margin-right: .4rem; }
.tester-exec .btn.exec-icone .c-icon { margin-right: 0; }
.tester-exec .btn.exec-icone { padding-left: .6rem; padding-right: .6rem; }
.tester-exec .form-control, .tester-exec .custom-select { font-size: .9375rem; }
/* O secondary do CoreUI (#ced2d8) deixa os botões neutros com cara de desabilitados: aqui eles ganham texto legível */
.tester-exec .btn-outline-secondary { border-color: #c4cad3; background-color: #fff; color: #36414f; }
.tester-exec .btn-outline-secondary:not(:disabled):hover, .tester-exec .btn-outline-secondary:not(:disabled):active,
.tester-exec .show > .btn-outline-secondary.dropdown-toggle { border-color: #aeb6c1; background-color: #eef0f3; color: #232e42; }
.tester-exec .btn-outline-secondary:disabled { border-color: #dde1e6; background-color: #fff; color: #7d8694; opacity: 1; }
.tester-exec .btn-ghost-secondary { color: #36414f; }
.tester-exec .btn-ghost-secondary:not(:disabled):hover, .tester-exec .show > .btn-ghost-secondary.dropdown-toggle { background-color: #e9ecf0; color: #232e42; }
.tester-exec .btn:focus-visible, .tester-exec .form-control:focus-visible, .tester-exec .custom-select:focus-visible,
.tester-exec button:focus-visible, .tester-exec .dropdown-item:focus-visible, .tester-exec input:focus-visible + .c-switch-slider {
    outline: 2px solid #321fdb;
    outline-offset: 2px;
    box-shadow: none;
}
/* top/left 0: ancorado no topo, o texto escondido nunca estica a rolagem (a da página ou a da tabela) */
.tester-exec .sr-only-exec { position: absolute; top: 0; left: 0; width: 1px; height: 1px; margin: -1px; padding: 0; overflow: hidden; clip: rect(0 0 0 0); border: 0; white-space: nowrap; }

/* Nome e etiquetas à esquerda, resumo compacto à direita */
.exec-cabecalho { display: grid; grid-template-columns: minmax(0, 1fr) minmax(22rem, 30rem); align-items: start; gap: .75rem 1.5rem; }
.exec-cabecalho-texto { min-width: 0; }
.exec-titulo-linha { display: flex; flex-wrap: wrap; align-items: baseline; gap: .25rem 1rem; }
.exec-titulo { margin: 0; font-size: 1.5625rem; font-weight: 600; line-height: 1.25; color: #232e42; overflow-wrap: anywhere; }
.exec-meta { display: flex; flex-wrap: wrap; gap: .4rem; margin: .65rem 0 0; padding: 0; list-style: none; }
.exec-meta li { display: inline-flex; align-items: center; gap: .3rem; padding: .2rem .65rem; border: 1px solid #dde1e7; border-radius: 999px; background: #fff; font-size: .875rem; color: #36414f; }
.exec-meta-rotulo { color: #56616f; }
.tester-exec .exec-editar { padding: .25rem 0; color: #321fdb; }

.exec-barra {
    position: sticky; top: 0; z-index: 6;
    display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .75rem 1rem;
    padding: .75rem 1rem; background: #fff; border: 1px solid #e1e5ea; border-radius: 8px;
}
.exec-barra-grupo { display: flex; flex-wrap: wrap; align-items: center; gap: .5rem; }
.tester-exec .exec-menu { min-width: 17rem; max-width: calc(100vw - 2rem); padding: .35rem 0; border-color: #dde1e7; border-radius: 8px; box-shadow: 0 8px 20px rgba(16, 24, 40, .1); font-size: .9375rem; }
.tester-exec .exec-menu .dropdown-item { display: flex; align-items: flex-start; gap: .65rem; width: 100%; padding: .55rem 1rem; border: 0; background: none; color: #2f3a4a; font-size: .9375rem; text-align: left; white-space: normal; cursor: pointer; }
.tester-exec .exec-menu .dropdown-item:hover:not(:disabled), .tester-exec .exec-menu .dropdown-item:focus:not(:disabled) { background: #f2f4f7; }
.tester-exec .exec-menu .dropdown-item:disabled { color: #626d7b; background: none; cursor: not-allowed; }
.tester-exec .exec-menu .dropdown-item .c-icon { flex-shrink: 0; margin-top: .2rem; }
.tester-exec .exec-menu .dropdown-item.exec-perigo:not(:disabled) { color: #b42318; }
.tester-exec .exec-menu .dropdown-divider { margin: .35rem 0; }
.exec-menu-texto { display: flex; flex-direction: column; min-width: 0; }
.exec-menu-motivo { font-size: .8125rem; color: #56616f; }
.exec-opcao { width: 22rem; max-width: 100%; padding: .75rem 1rem; }
.exec-opcao .tester-switch { font-size: .9375rem; font-weight: 500; color: #2f3a4a; }
.exec-opcao-ajuda { margin: .5rem 0 0; font-size: .8125rem; line-height: 1.45; color: #56616f; }

.exec-painel { padding: 1.25rem 1.5rem; background: #fff; border: 1px solid #e1e5ea; border-radius: 8px; }
/* Fundo cinza só na área da lista de resultados; a tabela (e os blocos no celular) ficam brancos por cima */
.exec-painel.exec-lista { background: #f4f5f7; border-color: #e4e7eb; }
.exec-secao-titulo { margin: 0; font-size: 1.125rem; font-weight: 600; line-height: 1.3; color: #232e42; }

.exec-resumo { display: grid; gap: .45rem; padding: .7rem .9rem; background: #fff; border: 1px solid #e1e5ea; border-radius: 8px; }
.exec-resumo-topo { display: flex; flex-wrap: wrap; align-items: baseline; justify-content: space-between; gap: .15rem .75rem; }
.exec-resumo-estado { display: flex; align-items: center; gap: .4rem; margin: 0; font-size: .9375rem; font-weight: 600; color: #232e42; }
.exec-resumo-estado .c-icon { width: 1rem; height: 1rem; }
.exec-resumo-andamento { font-size: .875rem; color: #36414f; }
.exec-totais { display: flex; flex-wrap: wrap; gap: .35rem; margin: 0; padding: 0; list-style: none; }
.exec-total { display: inline-flex; align-items: center; gap: .35rem; padding: .1rem .55rem; border-radius: 999px; background: #f2f4f7; font-size: .8125rem; color: #2f3a4a; }
.exec-ponto { flex-shrink: 0; width: .5rem; height: .5rem; border-radius: 50%; }
.exec-barra-mini { display: grid; grid-template-columns: minmax(4rem, 1fr) auto; align-items: center; gap: .6rem; }
.exec-barra-rotulo { font-size: .8125rem; color: #56616f; white-space: nowrap; }
.exec-trilho { display: flex; gap: 2px; height: 6px; overflow: hidden; border-radius: 999px; background: #e7eaee; }
.exec-trilho > span { flex-basis: 0; height: 100%; }
.exec-trilho > .exec-conclusao { flex: none; background: #3399ff; transition: width .4s ease; }
.tester-exec .sucesso > .exec-ponto, .exec-trilho > .sucesso { background: #2eb85c; }
.tester-exec .erro > .exec-ponto, .exec-trilho > .erro { background: #e55353; }
.tester-exec .interrompido > .exec-ponto, .exec-trilho > .interrompido { background: #f9b115; }
.tester-exec .cancelado > .exec-ponto, .exec-trilho > .cancelado { background: #8a93a2; }
.tester-exec .executando > .exec-ponto { background: #3399ff; }
.tester-exec .fila > .exec-ponto { background: #b9c0c9; }

/* A página não rola: a lista de resultados ocupa o resto da altura e só a tabela rola (cabeçalho das colunas fixo) */
@media (min-width: 768px) {
    .tester-exec .exec-painel.exec-lista { display: flex; flex: 1; flex-direction: column; min-height: 16rem; }
    /* sem position: os menus das linhas ficam fora da área de rolagem e viram para cima se faltar espaço */
    .exec-tabela-rolagem { flex: 1; min-height: 0; overflow: auto; border-radius: 8px; }
    /* o .dropdown do CoreUI é relative: sem isto o menu ⋮ da linha fica preso (e cortado) na rolagem da tabela */
    .exec-tabela .dropdown { position: static; }
    .exec-tabela thead th { position: sticky; top: 0; z-index: 2; }
}
.exec-lista-cabecalho { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: flex-start; gap: .75rem 1rem; }
.exec-mockup-comum { margin: .3rem 0 0; font-size: .875rem; color: #56616f; }
.exec-mockup-comum strong { font-weight: 600; color: #2f3a4a; }
.exec-segmentos { display: inline-flex; flex-wrap: wrap; padding: 3px; border-radius: 8px; background: #e4e7ec; }
.exec-segmentos button { padding: .35rem .9rem; border: 0; border-radius: 6px; background: transparent; font-size: .9375rem; color: #36414f; cursor: pointer; }
.exec-segmentos button[aria-pressed="true"] { background: #fff; color: #232e42; font-weight: 600; box-shadow: 0 1px 2px rgba(16, 24, 40, .14); }
.exec-filtros { display: flex; flex-wrap: wrap; align-items: center; gap: .75rem 1rem; margin: 1.1rem 0 1rem; }
.exec-busca { position: relative; flex: 1 1 280px; max-width: 460px; }
.exec-busca .c-icon { position: absolute; top: 50%; left: .75rem; transform: translateY(-50%); color: #56616f; pointer-events: none; }
.tester-exec .exec-busca input { padding-left: 2.25rem; }
.exec-chips { display: flex; flex-wrap: wrap; gap: .4rem; }
.exec-chip { display: inline-flex; align-items: center; gap: .45rem; padding: .3rem .85rem; border: 1px solid #d3d8df; border-radius: 999px; background: #fff; font-size: .9375rem; line-height: 1.35; color: #2f3a4a; cursor: pointer; }
.exec-chip:hover { border-color: #a7b0bc; }
.exec-chip[aria-pressed="true"] { border-color: #321fdb; background: #efedfd; color: #25169e; font-weight: 600; }
.exec-chip-contagem { min-width: 1.5rem; padding: 0 .4rem; border-radius: 999px; background: #eceff3; font-size: .8125rem; font-weight: 600; text-align: center; }
.exec-chip[aria-pressed="true"] .exec-chip-contagem { background: #fff; }
.exec-ordem { display: inline-flex; align-items: center; gap: .5rem; margin: 0 0 0 auto; font-size: .875rem; color: #56616f; }
.tester-exec .exec-ordem select { width: auto; }

.exec-tabela { width: 100%; border-collapse: separate; border-spacing: 0; border: 1px solid #e1e5ea; border-radius: 8px; background: #fff; font-size: .9375rem; }
.exec-tabela tbody > tr:last-child > td { border-bottom: 0; }
.exec-tabela tbody > tr:last-child > td:first-child { border-bottom-left-radius: 8px; }
.exec-tabela tbody > tr:last-child > td:last-child { border-bottom-right-radius: 8px; }
.exec-tabela th { padding: .6rem .9rem; border-bottom: 1px solid #e1e5ea; background: #f7f8fa; font-size: .875rem; font-weight: 600; color: #4a5565; text-align: left; white-space: nowrap; }
.exec-tabela th:first-child { border-top-left-radius: 8px; }
.exec-tabela th:last-child { border-top-right-radius: 8px; }
.exec-tabela td { padding: .8rem .9rem; border-bottom: 1px solid #eceff3; vertical-align: middle; }
.exec-linha:hover > td { background: #fafbfc; }
.exec-linha.aberta > td { background: #f6f7fa; border-bottom-color: transparent; }
.exec-caso-nome { font-weight: 600; color: #232e42; overflow-wrap: anywhere; }
.exec-caso-sub { display: flex; flex-wrap: wrap; gap: .15rem .9rem; margin-top: .2rem; font-size: .875rem; color: #56616f; overflow-wrap: anywhere; }
.exec-codigo { font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: .8125rem; color: #36414f; }
.exec-tag { display: inline-flex; align-items: center; gap: .35rem; padding: .2rem .6rem; border: 1px solid transparent; border-radius: 6px; font-size: .875rem; font-weight: 600; line-height: 1.4; white-space: nowrap; }
.exec-tag .c-icon { width: 1rem; height: 1rem; }
.exec-tag.sucesso { border-color: #c3e8cf; background: #e8f6ed; color: #1a6b34; }
.exec-tag.erro { border-color: #f5c8c5; background: #fdeeed; color: #a61f19; }
.exec-tag.interrompido { border-color: #f3dca6; background: #fff6e2; color: #744a00; }
.exec-tag.cancelado, .exec-tag.fila { border-color: #dde1e6; background: #f1f3f6; color: #3f4957; }
.exec-tag.executando { border-color: #c6dcfb; background: #e9f2ff; color: #1452a8; }
.exec-contadores { display: flex; flex-wrap: wrap; gap: .1rem .8rem; margin-top: .3rem; font-size: .875rem; color: #4a5565; }
.exec-contadores .ok strong { color: #1a6b34; }
.exec-contadores .falhou strong { color: #a61f19; }
.exec-aguardando { margin-top: .3rem; font-size: .875rem; color: #56616f; }
.exec-tabela .exec-col-duracao { width: 1%; text-align: right; white-space: nowrap; font-variant-numeric: tabular-nums; }
.exec-tabela .exec-col-acoes { width: 1%; text-align: right; white-space: nowrap; }
.exec-acoes-linha { display: flex; align-items: center; justify-content: flex-end; gap: .4rem; }

.exec-detalhes-linha > td { padding: 0 .9rem 1rem; background: #f6f7fa; border-bottom: 1px solid #e1e5ea; }
.exec-detalhes { padding: 1rem 1.25rem; border: 1px solid #e1e5ea; border-radius: 8px; background: #fff; }
.exec-detalhes-grade { display: grid; grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); gap: .9rem 1.5rem; margin: 0; }
.exec-detalhes-grade dt { margin-bottom: .15rem; font-size: .8125rem; font-weight: 600; color: #56616f; }
.exec-detalhes-grade dd { margin: 0; font-size: .9375rem; color: #2f3a4a; overflow-wrap: anywhere; }
.exec-indisponivel { color: #4a5565; }
.exec-indisponivel small { display: block; font-size: .8125rem; color: #56616f; }
.exec-detalhes-mensagens { margin-top: 1.1rem; }
.exec-detalhes-subtitulo { margin: 0 0 .5rem; font-size: .9375rem; font-weight: 600; color: #232e42; }
.exec-mensagens { max-height: 15rem; margin: 0; padding: .6rem .85rem; overflow: auto; list-style: none; border: 1px solid #eceff3; border-radius: 6px; background: #fbfbfc; font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace; font-size: .8125rem; line-height: 1.55; }
.exec-mensagens li { color: #9b1c16; white-space: pre-wrap; overflow-wrap: anywhere; }
.exec-mensagens li.aviso { color: #6b4400; }
.exec-detalhes-nota { margin: 0; font-size: .875rem; color: #56616f; }
.exec-detalhes-acoes { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: 1rem; }

.exec-rodape { display: flex; flex-wrap: wrap; justify-content: space-between; gap: .5rem 1rem; margin-top: .9rem; font-size: .875rem; color: #56616f; }
.exec-vazio { padding: 2.5rem 1rem; border: 1px dashed #d3d8df; border-radius: 8px; background: #fff; font-size: .9375rem; color: #4a5565; text-align: center; }
.exec-vazio .btn { margin-top: .75rem; }

@media (max-width: 991.98px) {
    .exec-cabecalho { grid-template-columns: minmax(0, 1fr); }
}
@media (max-width: 767.98px) {
    .exec-barra { position: static; }
    .exec-busca { flex-basis: 100%; max-width: none; }
    .exec-ordem { width: 100%; margin-left: 0; }
    .tester-exec .exec-ordem select { flex: 1; }
    .exec-tabela thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }
    .exec-tabela, .exec-tabela tbody, .exec-tabela tr, .exec-tabela td { display: block; width: 100%; }
    .exec-tabela { border: 0; background: transparent; }
    .exec-linha { margin-bottom: .75rem; border: 1px solid #e1e5ea; border-radius: 8px; background: #fff; }
    .exec-tabela .exec-linha > td { width: 100%; padding: .45rem 1rem; border: 0; text-align: left; white-space: normal; }
    .exec-linha > td::before { content: attr(data-label); display: block; margin-bottom: .2rem; font-size: .8125rem; font-weight: 600; color: #56616f; }
    .exec-linha > td:first-child { padding-top: .9rem; }
    .exec-linha > td:last-child { padding-bottom: .9rem; }
    .exec-acoes-linha { flex-wrap: wrap; justify-content: flex-start; }
    .exec-linha.aberta { margin-bottom: 0; border-bottom-style: dashed; border-radius: 8px 8px 0 0; }
    .exec-detalhes-linha { margin-bottom: .75rem; overflow: hidden; border: 1px solid #e1e5ea; border-top: 0; border-radius: 0 0 8px 8px; }
    .exec-tabela .exec-detalhes-linha > td { padding: .75rem; border: 0; border-radius: 0; }
    .exec-detalhes { padding: .85rem; }
}
@media (max-width: 575.98px) {
    .tester-page.tester-exec { padding: 1rem; }
    .exec-painel { padding: 1rem; }
    .exec-barra { padding: .75rem; }
}
`;

const ExecuteTaskAnalysisForm = () => {
    const {id} = useParams<GetParam>();
    const history = useHistory();
    const [showAlert] = useAlert();
    const aviso = (msg: string, type: 'success' | 'warning' | 'danger' = 'success') => showAlert({msg, type, toaster: true});

    const [plano, setPlano] = useState<Plano | null>(null);
    const [erroPlano, setErroPlano] = useState<string | null>(null);
    const {results, carregando, erro, atualizadoEm, temAtivos, recarregar} = useExecutionPolling(id);

    const [abrirJanela, setAbrirJanela] = useState<boolean>(lerPreferenciaJanela);
    const [enviando, setEnviando] = useState<boolean>(false);

    const [busca, setBusca] = useState<string>('');
    const [filtro, setFiltro] = useState<FiltroSituacao>('todos');
    const [somenteUltima, setSomenteUltima] = useState<boolean>(true);
    const [ordem, setOrdem] = useState<OrdemResultados>('recentes');
    const [abertos, setAbertos] = useState<Array<number>>([]);
    const [registros, setRegistros] = useState<Record<number, RegistroDetalhe>>({});

    const [confirmacao, setConfirmacao] = useState<Confirmacao | null>(null);
    const [processandoConfirmacao, setProcessandoConfirmacao] = useState<boolean>(false);
    const [log, setLog] = useState<{aberto: boolean, carregando: boolean, titulo: string, dados: ResultLog | null}>({aberto: false, carregando: false, titulo: '', dados: null});
    const [video, setVideo] = useState<{url: string, id: number} | null>(null);
    const [relatorioIA, setRelatorioIA] = useState<string | null>(null);
    const [agora, setAgora] = useState<Date>(new Date());

    // Envio um-por-vez ao Selenium: para quando o usuário cancela ou sai da tela
    const pararLegadoRef = useRef<boolean>(false);

    const isAI = Number(plano?.type) === TypeExecuteTask.TYPE_AI_TEST;
    const isCypress = Number(plano?.stack) === StackExecuteTask.CYPRESS;
    const isSelenium = Number(plano?.stack) === StackExecuteTask.SELENIUM;
    const simultaneo = plano?.executarSimultaneo === true;
    // Quantos a fila do admin deixa rodando juntos
    const limiteEfetivo = simultaneo ? (Number(plano?.limiteSimultaneo) || LIMITE_SIMULTANEO_PADRAO) : 1;
    const textoLimite = simultaneo ? 'até ' + limiteEfetivo + ' ao mesmo tempo' : 'um por vez, na ordem do plano';

    useEffect(() => {
        getApi().get('execute-task/get/' + id)
            .then(response => setPlano(response.data))
            .catch(() => setErroPlano('Não foi possível carregar o plano de execução.'));
        return () => {
            pararLegadoRef.current = true;
        };
    }, [id]);

    useEffect(() => {
        try {
            window.localStorage.setItem(PREFERENCIA_JANELA, abrirJanela ? '1' : '0');
        } catch (e) {
            // navegador sem localStorage: a preferência só não é lembrada
        }
    }, [abrirJanela]);

    // Relógio para a duração dos casos em execução
    useEffect(() => {
        if (!temAtivos) {
            return;
        }
        const timer = window.setInterval(() => setAgora(new Date()), 1000);
        return () => window.clearInterval(timer);
    }, [temAtivos]);

    // ------------------------------------------------------------------ execução

    const enviarParaMaquinaLocal = async (resposta: any) => {
        const params: Array<any> = resposta.params || [];

        if (isSelenium) {
            pararLegadoRef.current = false;
            executarLegadoUmPorVez(EXECUTOR_SELENIUM_URL, params, () => pararLegadoRef.current).then(() => recarregar());
            aviso(params.length + ' caso(s) enviados ao executor Selenium desta máquina. Mantenha esta tela aberta até o fim.');
            return;
        }

        await repassarAoExecutorLocal(params);
        aviso(resposta.total + ' caso(s) na fila: o executor desta máquina roda ' + textoLimite + '.'
            + (resposta.ignorados ? ' ' + resposta.ignorados + ' já estava(m) na fila.' : ''));
    }

    /**
     * Com janela o backend não alcança o executor desta máquina: a tela entrega o que o backend liberou (a fila e o
     * limite ficam no backend). Sem executor, fecha como interrompidos os entregues agora e os que ainda esperavam.
     */
    const repassarAoExecutorLocal = async (params: Array<any>) => {
        if (!params.length) {
            return;
        }
        const envio = await enviarCasosExecutorLocal(params, retrieveToken(), true);
        if (envio.semExecutor) {
            const esperando = resultsRef.current
                .filter(aguardaEntregaLocal)
                .map(r => r.id);
            await getApi().post('execute-task/cancel/' + id, {
                ids: [...params.map(p => p.id), ...esperando], motivo: 'interrompido', mensagem: 'o executor desta máquina não respondeu.'
            });
            await recarregar();
            aviso('O executor local não respondeu em ' + EXECUTOR_LOCAL_URL + '. Abra o Panoverse Cypress Executor nesta máquina e tente de novo.', 'danger');
            return;
        }
        // Recusados pelo executor (payload inválido): fecham como interrompidos, com o motivo no registro
        const porMotivo: Record<string, Array<number>> = {};
        envio.recusados.forEach(recusado => (porMotivo[recusado.motivo] = porMotivo[recusado.motivo] || []).push(recusado.id));
        for (const motivo of Object.keys(porMotivo)) {
            await getApi().post('execute-task/cancel/' + id, {ids: porMotivo[motivo], motivo: 'interrompido', mensagem: motivo});
        }
        if (envio.recusados.length) {
            await recarregar();
            aviso(envio.recusados.length + ' caso(s) recusado(s) pelo executor desta máquina: veja os detalhes do caso.', 'warning');
        }
    }

    const executar = async (reexecutarIds?: Array<number>) => {
        if (!plano || enviando) {
            return;
        }
        setEnviando(true);
        try {
            const api = getApi();
            const response = reexecutarIds
                ? await api.put('execute-task/execute-results', {ids: reexecutarIds, open_screen: abrirJanela})
                : await api.put('execute-task/execute/' + id + '?open_screen=' + abrirJanela);
            const resposta = response.data || {};

            if (!resposta.success) {
                aviso(resposta.message || 'Não foi possível iniciar a execução.', 'warning');
                await recarregar();
                return;
            }
            await recarregar();

            if (resposta.dispatched) {
                const executor = resposta.executor || {};
                const recusados = (executor.rejected || []).length;
                aviso(
                    resposta.total + ' caso(s) na fila do servidor: o executor roda ' + textoLimite + '.'
                    + (resposta.ignorados ? ' ' + resposta.ignorados + ' já estava(m) na fila.' : '')
                    + (recusados ? ' ' + recusados + ' recusado(s): veja os detalhes do caso.' : ''),
                    recusados ? 'warning' : 'success'
                );
            } else if ((resposta.params || []).length > 0) {
                await enviarParaMaquinaLocal(resposta);
            }
        } catch (error) {
            aviso('Erro ao iniciar a execução. Tente de novo; se persistir, veja se o executor está no ar.', 'danger');
        } finally {
            setEnviando(false);
        }
    }

    const cancelarExecucao = async () => {
        pararLegadoRef.current = true;
        const response = await getApi().post('execute-task/cancel/' + id, {ids: [], motivo: 'cancelado'});
        const resposta = response.data || {};
        if (!resposta.success) {
            aviso(resposta.message || 'Não foi possível cancelar.', 'danger');
            return;
        }
        aviso(resposta.message + (resposta.aviso ? ' ' + resposta.aviso : ''), resposta.aviso ? 'warning' : 'success');
        await recarregar();
    }

    // Com janela: enquanto houver caso esperando entrega, pede ao backend os que cabem no limite e repassa
    const resultsRef = useRef<Array<ExecutionResult>>(results);
    resultsRef.current = results;
    const repassandoRef = useRef<boolean>(false);
    // A lista mudou durante um pedido: pede de novo ao terminar (a vaga pode ter aberto no meio)
    const pedirDeNovoRef = useRef<boolean>(false);
    const faltamEnviar = results.filter(aguardaEntregaLocal).length;

    useEffect(() => {
        if (!faltamEnviar || enviando) {
            return;
        }
        if (repassandoRef.current) {
            pedirDeNovoRef.current = true;
            return;
        }
        repassandoRef.current = true;
        (async () => {
            try {
                do {
                    pedirDeNovoRef.current = false;
                    const response = await getApi().post('execute-task/proximos/' + id);
                    const params = response.data?.params || [];
                    if (params.length) {
                        await repassarAoExecutorLocal(params);
                        await recarregar();
                    }
                } while (pedirDeNovoRef.current && resultsRef.current.some(aguardaEntregaLocal));
            } catch (e) {
                // tenta de novo na próxima atualização da lista
            } finally {
                repassandoRef.current = false;
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results, enviando]);

    // ------------------------------------------------------------------ ações de resultado

    const abrirRegistroCompleto = useCallback((resultado: ExecutionResult) => {
        setLog({aberto: true, carregando: true, titulo: resultado.testCase || ('Resultado #' + resultado.id), dados: null});
        getApi().get('execute-task/result-log/' + resultado.id)
            .then(response => setLog(atual => ({...atual, carregando: false, dados: response.data})))
            .catch(() => {
                setLog(atual => ({...atual, aberto: false, carregando: false}));
                aviso('Não foi possível carregar os detalhes do caso.', 'danger');
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const carregarRegistro = useCallback((resultado: ExecutionResult) => {
        const versao = versaoDoResultado(resultado);
        setRegistros(atual => ({...atual, [resultado.id]: {carregando: true, erro: false, dados: atual[resultado.id]?.dados || null, versao}}));
        getApi().get('execute-task/result-log/' + resultado.id)
            .then(response => setRegistros(atual => ({...atual, [resultado.id]: {carregando: false, erro: false, dados: response.data, versao}})))
            .catch(() => setRegistros(atual => ({...atual, [resultado.id]: {carregando: false, erro: true, dados: null, versao}})));
    }, []);

    const alternarDetalhes = (resultado: ExecutionResult) => {
        if (abertos.includes(resultado.id)) {
            setAbertos(atual => atual.filter(abertoId => abertoId !== resultado.id));
            return;
        }
        setAbertos(atual => [...atual, resultado.id]);
        carregarRegistro(resultado);
    }

    // Detalhe aberto de um caso que mudou (terminou, contou mais um teste): lê o registro de novo
    useEffect(() => {
        abertos.forEach(abertoId => {
            const resultado = results.find(r => r.id === abertoId);
            const registro = registros[abertoId];
            if (resultado && registro && !registro.carregando && registro.versao !== versaoDoResultado(resultado)) {
                carregarRegistro(resultado);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results]);

    const abrirRelatorio = (resultado: ExecutionResult) => {
        // Abre a aba já no clique: navegador bloqueia window.open feito depois de uma requisição
        const aba = window.open('', '_blank');
        getApi().get('/cypress/reports/view-report-url/' + resultado.id)
            .then(response => {
                if (response.data?.reportUrl && aba) {
                    aba.location.href = response.data.reportUrl;
                } else {
                    aba?.close();
                }
            })
            .catch(() => {
                aba?.close();
                aviso('Relatório não encontrado para este caso.', 'warning');
            });
    }

    const abrirVideo = (resultado: ExecutionResult) => {
        getApi().get('/cypress/reports/view-report-video/' + resultado.id)
            .then(response => {
                if (response.data?.videoUrl) {
                    setVideo({url: response.data.videoUrl, id: resultado.id});
                } else {
                    aviso('Vídeo não encontrado para este caso.', 'warning');
                }
            })
            .catch(() => aviso('Vídeo não encontrado para este caso.', 'warning'));
    }

    const abrirRelatorioIA = (resultado: ExecutionResult) => {
        getApi().get('execute-task/result-log/' + resultado.id)
            .then(response => setRelatorioIA(response.data?.content || '_(sem conteúdo disponível)_'))
            .catch(() => aviso('Não foi possível carregar o relatório.', 'danger'));
    }

    const baixarDocumentoIA = (resultado: ExecutionResult) => {
        const host = (process.env.REACT_APP_HOST || '').replace(/\/$/, '');
        window.open(host + '/execute-task/result-document/' + resultado.id + '?_system_token=' + retrieveToken(), '_blank');
    }

    const confirmar = async () => {
        if (!confirmacao) {
            return;
        }
        setProcessandoConfirmacao(true);
        try {
            const api = getApi();
            if (confirmacao.tipo === 'cancelar') {
                await cancelarExecucao();
            } else {
                const response = confirmacao.tipo === 'excluirTodos'
                    ? await api.delete('execute-task/delete-all/' + id)
                    : await api.delete('execute-task/delete-result/' + confirmacao.resultado.id);
                if (response.data?.success === false) {
                    aviso(response.data.message || 'Não foi possível excluir.', 'warning');
                } else {
                    aviso(confirmacao.tipo === 'excluirTodos' ? 'Resultados excluídos.' : 'Resultado excluído.');
                }
                await recarregar();
            }
            setConfirmacao(null);
        } catch (error) {
            aviso('A operação falhou. Tente de novo.', 'danger');
        } finally {
            setProcessandoConfirmacao(false);
        }
    }

    // ------------------------------------------------------------------ lista

    const ultimas = useMemo(() => ultimaExecucaoPorCaso(results), [results]);
    const historico = useMemo(() => contarHistorico(results), [results]);
    const base = somenteUltima ? ultimas : results;
    const contagens = useMemo(() => resumoSituacoes(base), [base]);
    const situacaoAtual = useMemo(() => resumoSituacoes(ultimas), [ultimas]);
    // Regra da reexecução: casos (caso × mockup) cuja última execução terminou com erro ou foi interrompida
    const falhas = useMemo(() => ultimas.filter(isFalha), [ultimas]);

    const posicaoNoPlano = useMemo(() => {
        const casos: Record<string, number> = {};
        const mockups: Record<string, number> = {};
        (plano?.testCase || []).forEach((caso: any, index: number) => casos[String(caso.id)] = index);
        (plano?.mockup || []).forEach((mockup: any, index: number) => mockups[String(mockup.id)] = index);
        return (resultado: ExecutionResult) =>
            (casos[String(resultado.testCaseId)] ?? 9999) * 100 + (mockups[String(resultado.mockupId)] ?? 99);
    }, [plano]);

    const visiveis = useMemo(() => {
        const termo = normalizarBusca(busca);
        let lista = base.filter(resultado =>
            (filtro === 'todos' || situacaoResultado(resultado) === filtro)
            && (!termo || textoBusca(resultado).includes(termo))
        );
        if (ordem === 'plano') {
            lista = [...lista].sort((a, b) => posicaoNoPlano(a) - posicaoNoPlano(b) || b.id - a.id);
        } else if (ordem === 'erros') {
            const peso = (r: ExecutionResult) => (isFalha(r) ? 0 : isAtivo(r) ? 1 : 2);
            lista = [...lista].sort((a, b) => peso(a) - peso(b) || b.id - a.id);
        }
        return lista;
    }, [base, filtro, busca, ordem, posicaoNoPlano]);

    // Mockup igual em todos os resultados: aparece uma vez embaixo do título, e não em cada linha
    const mockupComum = useMemo(() => {
        const chaves = new Set(base.map(r => (r.mockupId !== null && r.mockupId !== undefined ? 'id:' + r.mockupId : 'nome:' + (r.mockup || ''))));
        return base.length > 0 && chaves.size === 1 ? base[0].mockup : null;
    }, [base]);

    const bloqueado = enviando || !plano;
    const qtdCasos = (plano?.testCase || []).length;
    const qtdMockups = (plano?.mockup || []).length;
    const filtrosAtivos = filtro !== 'todos' || busca.trim() !== '';

    // ------------------------------------------------------------------ resumo

    const totalEscopo = base.length;
    const ativosEscopo = contagens.fila + contagens.executando;
    const finalizados = totalEscopo - ativosEscopo;
    const estado = situacaoAtual.executando > 0 ? {titulo: 'Em execução', icone: null}
        : situacaoAtual.fila > 0 ? {titulo: 'Na fila', icone: 'cil-clock'}
        : situacaoAtual.cancelado > 0 ? {titulo: 'Execução cancelada', icone: 'cil-ban'}
        : {titulo: 'Execução concluída', icone: 'cil-check-circle'};
    const unidadeFinalizada = somenteUltima
        ? (totalEscopo === 1 ? 'caso finalizado' : 'casos finalizados')
        : (totalEscopo === 1 ? 'execução finalizada' : 'execuções finalizadas');
    const ritmo = 'Executando ' + textoLimite;
    const anteriores = results.length - ultimas.length;
    const textoEscopo = somenteUltima
        ? 'Considerando a última execução de cada caso'
            + (qtdMockups > 1 ? ' (cada combinação de caso e mockup conta uma vez)' : '')
            + (anteriores > 0 ? '. ' + plural(anteriores, 'execução anterior fica', 'execuções anteriores ficam') + ' de fora; veja em "Todas as execuções".' : '.')
        : 'Considerando todas as execuções, inclusive as anteriores: '
            + plural(results.length, 'tentativa', 'tentativas') + ' de ' + plural(ultimas.length, 'caso', 'casos') + '.';

    // ------------------------------------------------------------------ artefatos

    const motivoSemArtefato = (resultado: ExecutionResult): string => {
        if (isAtivo(resultado)) {
            return 'Disponível quando o caso terminar';
        }
        if (isAI) {
            return 'Relatório ainda não recebido';
        }
        if (situacaoResultado(resultado) === 'cancelado') {
            return 'Execução cancelada: relatório não recebido';
        }
        if (plano && !plano.gerarRelatorio) {
            return 'O plano está com "Gerar relatório" desligado';
        }
        return 'Relatório não recebido pelo admin';
    }

    const artefatos = (resultado: ExecutionResult) => (isAI
        ? [
            {chave: 'relatorio', rotulo: 'Relatório do agente', icone: 'cil-description', acao: () => abrirRelatorioIA(resultado)},
            {chave: 'json', rotulo: 'Baixar JSON', icone: 'cil-cloud-download', acao: () => baixarDocumentoIA(resultado)},
        ]
        : [
            {chave: 'relatorio', rotulo: 'Relatório', icone: 'cil-description', acao: () => abrirRelatorio(resultado)},
            {chave: 'video', rotulo: 'Vídeo', icone: 'cil-video', acao: () => abrirVideo(resultado)},
        ]);

    // ------------------------------------------------------------------ partes da tela

    const renderTag = (resultado: ExecutionResult) => {
        const situacao = situacaoResultado(resultado);
        const icone = ICONE_SITUACAO[situacao];
        return (
            <span className={'exec-tag ' + situacao}>
                {icone ? <CIcon name={icone} aria-hidden="true"/> : <CSpinner size="sm" style={{width: '.85rem', height: '.85rem'}} aria-hidden="true"/>}
                {SITUACOES[situacao].label}
            </span>
        );
    }

    const renderContadores = (resultado: ExecutionResult) => {
        const ok = Number(resultado.amount_success) || 0;
        const erros = Number(resultado.amount_erros) || 0;
        if (ok === 0 && erros === 0) {
            return null;
        }
        return (
            <div className="exec-contadores" title="Testes do Cypress (blocos it) que o executor informou nesta execução">
                {ok > 0 && <span className="ok"><strong>{ok}</strong> {ok === 1 ? 'teste OK' : 'testes OK'}</span>}
                {erros > 0 && <span className="falhou"><strong>{erros}</strong> {erros === 1 ? 'teste com erro' : 'testes com erro'}</span>}
            </div>
        );
    }

    const renderDetalhes = (resultado: ExecutionResult) => {
        const ativo = isAtivo(resultado);
        const registro = registros[resultado.id];
        const mensagensFalha = (registro?.dados?.messages || []).filter(mensagem => MENSAGEM_FALHA.test(mensagem));
        const duracao = duracaoResultado(resultado, agora);
        return (
            <div className="exec-detalhes">
                <dl className="exec-detalhes-grade">
                    <div><dt>Mockup</dt><dd>{resultado.mockup || 'Sem mockup'}</dd></div>
                    <div><dt>Início</dt><dd>{resultado.dataHoraInicio || (ativo ? 'Ainda não começou' : 'Não registrado')}</dd></div>
                    <div><dt>Fim</dt><dd>{resultado.dataHoraFim || (ativo ? 'Em andamento' : 'Não registrado')}</dd></div>
                    <div><dt>Duração</dt><dd>{duracao || '—'}</dd></div>
                    <div><dt>Código do caso</dt><dd>{resultado.codigo || '—'}</dd></div>
                    <div><dt>Execução</dt><dd>#{resultado.id} · {TypeExecuteTaskResultList.getDescription(Number(resultado.status)) || SITUACOES[situacaoResultado(resultado)].label}</dd></div>
                    <div><dt>Testes OK</dt><dd>{Number(resultado.amount_success) || 0}</dd></div>
                    <div><dt>Testes com erro</dt><dd>{Number(resultado.amount_erros) || 0}</dd></div>
                    {artefatos(resultado).map(artefato => (
                        <div key={artefato.chave}>
                            <dt>{artefato.rotulo}</dt>
                            <dd className={resultado.hasReport ? '' : 'exec-indisponivel'}>
                                {resultado.hasReport ? 'Disponível' : <>Indisponível<small>{motivoSemArtefato(resultado)}</small></>}
                            </dd>
                        </div>
                    ))}
                </dl>

                <div className="exec-detalhes-mensagens">
                    <h3 className="exec-detalhes-subtitulo">Mensagens de falha do registro</h3>
                    {registro?.carregando && !registro.dados && <p className="exec-detalhes-nota"><CSpinner size="sm" className="mr-2"/>Carregando o registro...</p>}
                    {registro?.erro && <p className="exec-detalhes-nota">Não foi possível carregar o registro desta execução.</p>}
                    {registro?.dados && mensagensFalha.length === 0 && (
                        <p className="exec-detalhes-nota">
                            {(registro.dados.messages || []).length === 0 ? 'O registro ainda não tem mensagens.' : 'Nenhuma mensagem de falha no registro.'}
                        </p>
                    )}
                    {mensagensFalha.length > 0 && (
                        <>
                            <ul className="exec-mensagens">
                                {mensagensFalha.slice(0, MAXIMO_MENSAGENS_FALHA).map((mensagem, index) => (
                                    <li key={index} className={/\[ERRO]|Detalhe:|Payload inválido/i.test(mensagem) ? '' : 'aviso'}>{mensagem}</li>
                                ))}
                            </ul>
                            {mensagensFalha.length > MAXIMO_MENSAGENS_FALHA && (
                                <p className="exec-detalhes-nota mt-2">
                                    Mais {mensagensFalha.length - MAXIMO_MENSAGENS_FALHA} mensagens no registro completo.
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div className="exec-detalhes-acoes">
                    <CButton size="sm" color="secondary" variant="outline" onClick={() => abrirRegistroCompleto(resultado)}>
                        <CIcon name="cil-list" aria-hidden="true"/>Registro completo, mockup e passos
                    </CButton>
                    {resultado.hasReport && artefatos(resultado).map(artefato => (
                        <CButton key={artefato.chave} size="sm" color="secondary" variant="outline" onClick={artefato.acao}>
                            <CIcon name={artefato.icone} aria-hidden="true"/>{artefato.rotulo}
                        </CButton>
                    ))}
                </div>
            </div>
        );
    }

    const renderLinha = (resultado: ExecutionResult) => {
        const situacao = situacaoResultado(resultado);
        const ativo = isAtivo(resultado);
        const aberto = abertos.includes(resultado.id);
        const nome = nomeDoCaso(resultado);
        const duracao = duracaoResultado(resultado, agora);
        const anterioresDoCaso = somenteUltima ? (historico[resultado.id] || 0) : 0;
        const idDetalhes = 'exec-detalhes-' + resultado.id;

        return (
            <React.Fragment key={resultado.id}>
                <tr className={'exec-linha situacao-' + situacao + (aberto ? ' aberta' : '')} data-resultado={resultado.id}>
                    <td data-label="Caso de teste">
                        <div className="exec-caso-nome">{nome}</div>
                        <div className="exec-caso-sub">
                            {resultado.codigo && <span className="exec-codigo">{resultado.codigo}</span>}
                            <span>Execução #{resultado.id}</span>
                            {!mockupComum && resultado.mockup && <span>Mockup: {resultado.mockup}</span>}
                            {anterioresDoCaso > 0 && <span>{plural(anterioresDoCaso, 'execução anterior', 'execuções anteriores')}</span>}
                        </div>
                    </td>
                    <td data-label="Resultado do caso">
                        {renderTag(resultado)}
                        {renderContadores(resultado)}
                        {situacao === 'fila' && <div className="exec-aguardando">{resultado.enviado ? 'Enviado ao executor, abrindo o Cypress' : 'Aguardando vaga na fila'}</div>}
                    </td>
                    <td data-label="Duração" className="exec-col-duracao">
                        {duracao || <><span aria-hidden="true">—</span><span className="sr-only-exec">{ativo ? 'Ainda não começou' : 'Sem duração registrada'}</span></>}
                    </td>
                    <td data-label="Ações" className="exec-col-acoes">
                        <div className="exec-acoes-linha">
                            <CButton size="sm" color="secondary" variant="outline" aria-expanded={aberto} aria-controls={idDetalhes}
                                     onClick={() => alternarDetalhes(resultado)}>
                                <CIcon name={aberto ? 'cil-chevron-top' : 'cil-chevron-bottom'} aria-hidden="true"/>Detalhes
                            </CButton>
                            {!isAI && (
                                <CButton size="sm" color="secondary" variant="outline" disabled={bloqueado || ativo}
                                         title={ativo ? 'Caso ainda na fila ou executando' : 'Reexecutar este caso com o mesmo mockup'}
                                         onClick={() => executar([resultado.id])}>
                                    <CIcon name="cil-reload" aria-hidden="true"/>Reexecutar
                                </CButton>
                            )}
                            <CDropdown>
                                <CDropdownToggle size="sm" color="secondary" variant="ghost" caret={false} className="exec-icone"
                                                 aria-label={'Mais ações: ' + nome} title="Mais ações">
                                    <CIcon name="cil-options" aria-hidden="true"/>
                                </CDropdownToggle>
                                <CDropdownMenu placement="bottom-end" className="exec-menu">
                                    {artefatos(resultado).map(artefato => (
                                        <CDropdownItem key={artefato.chave} tag="button" type="button" disabled={!resultado.hasReport} onClick={artefato.acao}>
                                            <CIcon name={artefato.icone} aria-hidden="true"/>
                                            <span className="exec-menu-texto">
                                                {artefato.rotulo}
                                                {!resultado.hasReport && <span className="exec-menu-motivo">{motivoSemArtefato(resultado)}</span>}
                                            </span>
                                        </CDropdownItem>
                                    ))}
                                    <CDropdownDivider/>
                                    <CDropdownItem tag="button" type="button" className="exec-perigo" disabled={ativo}
                                                   onClick={() => setConfirmacao({tipo: 'excluir', resultado})}>
                                        <CIcon name="cil-trash" aria-hidden="true"/>
                                        <span className="exec-menu-texto">
                                            Excluir resultado
                                            {ativo && <span className="exec-menu-motivo">Cancele a execução antes de excluir</span>}
                                        </span>
                                    </CDropdownItem>
                                </CDropdownMenu>
                            </CDropdown>
                        </div>
                    </td>
                </tr>
                {aberto && (
                    <tr className="exec-detalhes-linha" id={idDetalhes}>
                        <td colSpan={4}>{renderDetalhes(resultado)}</td>
                    </tr>
                )}
            </React.Fragment>
        );
    }

    const executor = isAI
        ? (StackExecuteTaskAIList.getDescription(Number(plano?.stack)) || 'Agente de IA')
        : StackExecuteTaskList.getDescription(Number(plano?.stack));
    const bloqueioExclusao = temAtivos ? 'Cancele a execução antes de excluir'
        : results.length === 0 ? 'Ainda não há resultados' : null;

    // ------------------------------------------------------------------ tela

    return (
        <div className="tester-page tester-exec">
            <style>{ESTILOS}</style>

            <header className="exec-cabecalho">
                <div className="exec-cabecalho-texto">
                    <div className="exec-titulo-linha">
                        <h1 className="exec-titulo">{plano?.name || (erroPlano ? 'Plano de execução' : 'Carregando...')}</h1>
                        <CButton color="link" className="exec-editar" onClick={() => history.push('/execute-task/alterar/' + id)} disabled={!plano}>
                            <CIcon name="cil-pencil" aria-hidden="true"/>Editar plano
                        </CButton>
                    </div>
                    {plano && (
                        <ul className="exec-meta" aria-label="Configuração do plano">
                            <li>
                                <span className="exec-meta-rotulo">Executor:</span>
                                {executor}{isCypress && (abrirJanela ? ' · esta máquina' : ' · servidor')}
                            </li>
                            {!isAI && <li><span className="exec-meta-rotulo">Execução:</span>{simultaneo ? 'até ' + limiteEfetivo + ' ao mesmo tempo' : 'um por vez'}</li>}
                            <li>
                                {plural(qtdCasos, 'caso', 'casos')} × {plural(qtdMockups, 'mockup', 'mockups')}
                                {!isAI && ' = ' + plural(qtdCasos * qtdMockups, 'execução', 'execuções')}
                            </li>
                            {isCypress && <li><span className="exec-meta-rotulo">Relatório:</span>{plano.gerarRelatorio ? 'com relatório e vídeo' : 'desligado'}</li>}
                        </ul>
                    )}
                </div>

                {/* Resumo compacto ao lado do nome: estado, andamento, totais e as barras finas */}
                {totalEscopo > 0 && (
                    <section className="exec-resumo" aria-labelledby="exec-resumo-estado" aria-live="polite" title={textoEscopo}>
                        <div className="exec-resumo-topo">
                            <h2 className="exec-resumo-estado" id="exec-resumo-estado">
                                {estado.icone ? <CIcon name={estado.icone} aria-hidden="true"/> : <CSpinner size="sm" color="info" aria-hidden="true"/>}
                                {estado.titulo}
                            </h2>
                            <span className="exec-resumo-andamento"><strong>{finalizados} de {totalEscopo}</strong> {unidadeFinalizada}</span>
                        </div>
                        <ul className="exec-totais" aria-label="Totais por resultado">
                            {(['sucesso', 'erro', 'interrompido', 'cancelado', 'executando', 'fila'] as Array<Situacao>)
                                .filter(situacao => contagens[situacao] > 0 || situacao === 'sucesso' || situacao === 'erro')
                                .map(situacao => (
                                    <li key={situacao} className={'exec-total ' + situacao}>
                                        <span className="exec-ponto" aria-hidden="true"/>{TOTAIS[situacao](contagens[situacao])}
                                    </li>
                                ))}
                        </ul>
                        {ativosEscopo > 0 && (
                            <div className="exec-barra-mini">
                                <div className="exec-trilho" role="progressbar" aria-labelledby="exec-rotulo-conclusao"
                                     aria-valuemin={0} aria-valuemax={totalEscopo} aria-valuenow={finalizados}>
                                    <span className="exec-conclusao" style={{width: (finalizados / totalEscopo) * 100 + '%'}}/>
                                </div>
                                <span className="exec-barra-rotulo" id="exec-rotulo-conclusao">
                                    Progresso da conclusão · {Math.round((finalizados / totalEscopo) * 100)}%{!isAI && ' · ' + ritmo}
                                </span>
                            </div>
                        )}
                        {finalizados > 0 && (
                            <div className="exec-barra-mini">
                                <div className="exec-trilho" role="img"
                                     aria-label={'Distribuição dos resultados: ' + ORDEM_DISTRIBUICAO.filter(s => contagens[s] > 0).map(s => TOTAIS[s](contagens[s])).join(', ')}>
                                    {ORDEM_DISTRIBUICAO.filter(situacao => contagens[situacao] > 0).map(situacao => (
                                        <span key={situacao} className={situacao} style={{flexGrow: contagens[situacao]}}/>
                                    ))}
                                </div>
                                <span className="exec-barra-rotulo">
                                    Distribuição dos resultados · {somenteUltima ? 'última de cada caso' : 'todas as execuções'}
                                </span>
                            </div>
                        )}
                        <span className="sr-only-exec">{textoEscopo}</span>
                    </section>
                )}
            </header>

            {erroPlano && <CAlert color="danger" className="mb-0">{erroPlano}</CAlert>}

            <div className="exec-barra" aria-label="Ações de execução">
                <div className="exec-barra-grupo">
                    {!isAI && (
                        <CButton color="primary" variant={falhas.length > 0 ? undefined : 'outline'}
                                 onClick={() => executar(falhas.map(f => f.id))} disabled={bloqueado || falhas.length === 0}
                                 title={falhas.length > 0
                                     ? 'Reexecutar só os casos cuja última execução terminou com erro ou foi interrompida'
                                     : 'Nenhum caso com falha na última execução'}>
                            <CIcon name="cil-reload" aria-hidden="true"/>Reexecutar falhas ({falhas.length})
                        </CButton>
                    )}
                    {!isAI && (
                        <CButton color="primary" variant={falhas.length > 0 ? 'outline' : undefined}
                                 onClick={() => executar()} disabled={bloqueado || temAtivos}
                                 title={temAtivos ? 'Aguarde a execução atual terminar ou cancele' : 'Executar todos os casos do plano'}>
                            {enviando ? <CSpinner size="sm" className="mr-2"/> : <CIcon name="cil-media-play" aria-hidden="true"/>}
                            Executar plano
                        </CButton>
                    )}
                    {temAtivos && !isAI && (
                        <CButton color="danger" variant="outline" onClick={() => setConfirmacao({tipo: 'cancelar'})}>
                            <CIcon name="cil-media-stop" aria-hidden="true"/>Cancelar execução
                        </CButton>
                    )}
                </div>

                <div className="exec-barra-grupo">
                    {!isAI && (
                        <CDropdown className="exec-opcoes">
                            <CDropdownToggle color="secondary" variant="outline" aria-label="Opções de execução">
                                <CIcon name="cil-settings" aria-hidden="true"/>Opções de execução
                            </CDropdownToggle>
                            <CDropdownMenu placement="bottom-end" className="exec-menu">
                                <div className="exec-opcao">
                                    <label className={'tester-switch' + (temAtivos || enviando ? ' desabilitado' : '')}>
                                        <CSwitch color="primary" shape="pill" checked={abrirJanela} disabled={temAtivos || enviando}
                                                 onChange={(e: any) => setAbrirJanela(e.target.checked)}/>
                                        Abrir janela do navegador
                                    </label>
                                    <p className="exec-opcao-ajuda">
                                        Ligado, os casos rodam no executor desta máquina ({EXECUTOR_LOCAL_URL}) e cada caso abre um navegador.
                                        Desligado, rodam no executor do servidor.
                                    </p>
                                    {(temAtivos || enviando) && (
                                        <p className="exec-opcao-ajuda">Não dá para trocar durante uma execução.</p>
                                    )}
                                </div>
                            </CDropdownMenu>
                        </CDropdown>
                    )}
                    <CButton color="secondary" variant="outline" className="exec-icone" onClick={() => recarregar()}
                             aria-label="Atualizar resultados" title="Atualizar resultados">
                        <CIcon name="cil-reload" aria-hidden="true"/>
                    </CButton>
                    <CDropdown className="exec-mais">
                        <CDropdownToggle color="secondary" variant="outline" caret={false} className="exec-icone"
                                         aria-label="Mais ações do plano" title="Mais ações">
                            <CIcon name="cil-options" aria-hidden="true"/>
                        </CDropdownToggle>
                        <CDropdownMenu placement="bottom-end" className="exec-menu">
                            {isCypress && (
                                <CDropdownItem tag="button" type="button" onClick={abrirCypressLocal}>
                                    <CIcon name="cil-screen-desktop" aria-hidden="true"/>
                                    <span className="exec-menu-texto">
                                        Abrir Cypress
                                        <span className="exec-menu-motivo">Abre o Cypress desta máquina (modo interativo)</span>
                                    </span>
                                </CDropdownItem>
                            )}
                            <CDropdownItem tag="button" type="button" className="exec-perigo" disabled={bloqueioExclusao !== null}
                                           onClick={() => setConfirmacao({tipo: 'excluirTodos'})}>
                                <CIcon name="cil-trash" aria-hidden="true"/>
                                <span className="exec-menu-texto">
                                    Excluir resultados
                                    <span className="exec-menu-motivo">{bloqueioExclusao || 'Apaga todos os resultados deste plano'}</span>
                                </span>
                            </CDropdownItem>
                        </CDropdownMenu>
                    </CDropdown>
                </div>
            </div>

            {erro && <CAlert color="warning" className="mb-0">{erro}</CAlert>}

            {faltamEnviar > 0 && (
                <CAlert color="info" className="mb-0">
                    Mantenha esta tela aberta: {faltamEnviar === 1 ? 'falta 1 caso' : 'faltam ' + faltamEnviar + ' casos'} para
                    entregar ao executor desta máquina. O admin vai liberando {textoLimite}.
                </CAlert>
            )}

            <section className="exec-painel exec-lista" aria-labelledby="exec-lista-titulo">
                <div className="exec-lista-cabecalho">
                    <div>
                        <h2 className="exec-secao-titulo" id="exec-lista-titulo">Resultados dos casos</h2>
                        {mockupComum && <p className="exec-mockup-comum">Mockup: <strong>{mockupComum}</strong></p>}
                    </div>
                    <div className="exec-segmentos" role="group" aria-label="Visualização dos resultados">
                        <button type="button" aria-pressed={somenteUltima} onClick={() => setSomenteUltima(true)}
                                title="Só o resultado mais recente de cada caso">
                            Última por caso
                        </button>
                        <button type="button" aria-pressed={!somenteUltima} onClick={() => setSomenteUltima(false)}
                                title="Cada reexecução cria um resultado novo: mostra o histórico completo">
                            Todas as execuções
                        </button>
                    </div>
                </div>

                {results.length > 0 && (
                    <div className="exec-filtros">
                        <div className="exec-busca">
                            <CIcon name="cil-search" aria-hidden="true"/>
                            <CInput
                                type="search"
                                value={busca}
                                onChange={(e: any) => setBusca(e.target.value)}
                                placeholder="Buscar por código, nome, mockup ou ID"
                                aria-label="Buscar resultados por código, nome, mockup ou ID"
                            />
                        </div>

                        <div className="exec-chips" role="group" aria-label="Filtrar por resultado">
                            <button type="button" className="exec-chip" aria-pressed={filtro === 'todos'} onClick={() => setFiltro('todos')}>
                                Todos<span className="exec-chip-contagem">{totalEscopo}</span>
                            </button>
                            {(['erro', 'sucesso'] as Array<Situacao>).concat(SITUACOES_EXTRAS.filter(s => contagens[s] > 0 || filtro === s)).map(situacao => (
                                <button key={situacao} type="button" className="exec-chip" aria-pressed={filtro === situacao}
                                        onClick={() => setFiltro(filtro === situacao ? 'todos' : situacao)}>
                                    {SITUACOES[situacao].label}<span className="exec-chip-contagem">{contagens[situacao]}</span>
                                </button>
                            ))}
                        </div>

                        <label className="exec-ordem">
                            Ordenar
                            <CSelect value={ordem} onChange={(e: any) => setOrdem(e.target.value)} aria-label="Ordenar resultados">
                                <option value="recentes">Mais recentes primeiro</option>
                                <option value="plano">Ordem do plano</option>
                                <option value="erros">Falhas primeiro</option>
                            </CSelect>
                        </label>
                    </div>
                )}

                {carregando && results.length === 0 && (
                    <div className="text-center p-4"><CSpinner color="primary"/></div>
                )}
                {!carregando && results.length === 0 && (
                    <div className="exec-vazio mt-3">
                        Nenhum resultado ainda.{!isAI && ' Use "Executar plano" para rodar os casos.'}
                    </div>
                )}
                {results.length > 0 && visiveis.length === 0 && (
                    <div className="exec-vazio">
                        Nenhum resultado com esses filtros.
                        <div>
                            <CButton color="secondary" variant="outline" onClick={() => { setFiltro('todos'); setBusca(''); }}>
                                Limpar filtros
                            </CButton>
                        </div>
                    </div>
                )}

                {visiveis.length > 0 && (
                    <div className="exec-tabela-rolagem">
                        <table className="exec-tabela">
                            <caption className="sr-only-exec">Resultados dos casos</caption>
                            <thead>
                                <tr>
                                    <th scope="col">Caso de teste</th>
                                    <th scope="col">Resultado do caso</th>
                                    <th scope="col" className="exec-col-duracao">Duração</th>
                                    <th scope="col" className="exec-col-acoes">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map(renderLinha)}
                            </tbody>
                        </table>
                    </div>
                )}

                {results.length > 0 && (
                    <div className="exec-rodape">
                        <span aria-live="polite">
                            {filtrosAtivos
                                ? visiveis.length + ' de ' + plural(totalEscopo, 'resultado exibido', 'resultados exibidos')
                                : plural(visiveis.length, 'resultado exibido', 'resultados exibidos')}
                        </span>
                        {atualizadoEm && <span>Atualizado às {atualizadoEm.toLocaleTimeString('pt-BR')}</span>}
                    </div>
                )}
            </section>

            <ResultLogModal
                aberto={log.aberto}
                carregando={log.carregando}
                titulo={log.titulo}
                log={log.dados}
                onFechar={() => setLog({aberto: false, carregando: false, titulo: '', dados: null})}
            />

            <CModal show={video !== null} onClose={() => setVideo(null)} size="xl">
                <CModalHeader closeButton>
                    <CModalTitle>Vídeo da execução #{video?.id}</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    {video && (
                        <video width="100%" controls autoPlay src={video.url}>
                            Seu navegador não suporta a tag de vídeo.
                        </video>
                    )}
                </CModalBody>
            </CModal>

            <CModal show={relatorioIA !== null} onClose={() => setRelatorioIA(null)} size="xl" scrollable>
                <CModalHeader closeButton>
                    <CModalTitle>Relatório de execução</CModalTitle>
                </CModalHeader>
                <CModalBody>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{relatorioIA || ''}</ReactMarkdown>
                </CModalBody>
            </CModal>

            <ConfirmModal
                aberto={confirmacao !== null}
                processando={processandoConfirmacao}
                titulo={
                    confirmacao?.tipo === 'cancelar' ? 'Cancelar execução'
                        : confirmacao?.tipo === 'excluirTodos' ? 'Excluir todos os resultados'
                        : 'Excluir resultado'
                }
                rotuloConfirmar={
                    confirmacao?.tipo === 'cancelar' ? 'Cancelar execução' : 'Excluir'
                }
                corConfirmar="danger"
                onConfirmar={confirmar}
                onFechar={() => !processandoConfirmacao && setConfirmacao(null)}
            >
                {confirmacao?.tipo === 'cancelar' && (
                    <p className="mb-0">
                        Os casos na fila ou executando ficam como cancelados. O executor não interrompe o que já recebeu:
                        esses casos podem terminar de rodar, mas o resultado continua cancelado. Os já concluídos continuam na lista.
                    </p>
                )}
                {confirmacao?.tipo === 'excluirTodos' && (
                    <p className="mb-0">
                        Todos os {results.length} resultados deste plano, com relatórios e vídeos, serão apagados. Essa ação não tem volta.
                    </p>
                )}
                {confirmacao?.tipo === 'excluir' && (
                    <p className="mb-0">
                        O resultado #{confirmacao.resultado.id} ({confirmacao.resultado.testCase}), com relatório e vídeo, será apagado.
                    </p>
                )}
            </ConfirmModal>
        </div>
    )
}

export default ExecuteTaskAnalysisForm;
